import { CTAButton } from "@/components/Button";
import { dropdownArrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { ExtensionOutdatedBanner } from "@/pages/ClubImportTutorial/ExtensionOutdatedBanner";
import { addAlert } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

interface SecondPartSectionProps {
  canStartImport: boolean;
  onStartImport: () => void;
}

// One row is the point: at 240px the cards hold together down to a 1152px
// viewport, where 316px ones wrapped into two unrelated-looking groups.
const STEPS = [
  {
    image: "import-step-1.png",
    n: 1,
    text: (
      <p>
        <Trans
          ns="clubImport"
          i18nKey="tutorial.secondPart.step1"
          components={{ hl: <span className="text-primary-blue font-bold" /> }}
        />
      </p>
    ),
  },
  {
    image: "desktop-login-2.png",
    n: 2,
    text: "tutorial.secondPart.step2",
  },
  {
    image: "import-step-3.png",
    n: 3,
    text: "tutorial.secondPart.step3",
  },
] as const;

export const SecondPartSection = ({
  canStartImport,
  onStartImport,
}: SecondPartSectionProps) => {
  const { t } = useTranslation(["clubImport", "common"]);
  const dispatch = useDispatch<AppDispatch>();

  /*
    Folded until the checklist above is cleared — instructions for something
    that cannot be started yet only push the actionable steps out of view.
  */
  const ready = canStartImport;
  const [manuallyToggled, setManuallyToggled] = useState<boolean | null>(null);
  const expanded = ready && (manuallyToggled ?? true);

  // Clearing the first part hands control back to the section: whatever the
  // user chose while they were still blocked no longer describes where they are.
  useEffect(() => {
    if (ready) setManuallyToggled(null);
  }, [ready]);

  const handleStartImportClick = () => {
    if (canStartImport) {
      onStartImport();
      return;
    }
    dispatch(
      addAlert({
        type: "error",
        title: t("common:clubImport.alertTitle"),
        description: t("alerts.finishFirstPart"),
      }),
    );
  };

  const toggle = () => {
    if (ready) setManuallyToggled(!expanded);
  };

  return (
    <section className="mb-12 p-12 bg-panel-background rounded-3xl">
      <div
        onClick={toggle}
        className={cn(
          "w-full flex items-center justify-between mb-4",
          ready && "cursor-pointer",
        )}
      >
        <div className="flex items-baseline gap-x-3">
          <h2 className="text-white text-xl font-semibold">
            {t("tutorial.secondPart.title")}
          </h2>
          {!ready && (
            <span className="text-dark-tertiary text-sm">
              {t("tutorial.secondPart.finishFirstPart")}
            </span>
          )}
        </div>
        <div
          className={cn(
            "transition-transform duration-300 ease-in-out [&_svg_path]:stroke-white",
            !expanded && "rotate-180",
          )}
        >
          {dropdownArrow}
        </div>
      </div>
      <div
        // The fold hides by height, which leaves what it hides focusable — and
        // what it hides now includes Start Import.
        inert={!expanded}
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          expanded ? "max-h-[2000px]" : "max-h-0",
        )}
      >
        <ExtensionOutdatedBanner className="mb-8" />
        <div className="flex flex-wrap gap-6">
          {STEPS.map((step) => (
            <div key={step.n} className="w-[240px] shrink-0">
              {/* Height comes from the image's own ratio rather than a second
                  hardcoded number, so the width above is the only thing to
                  change if these are resized again. */}
              {/* <img
                className="w-full h-auto mb-4"
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/import-tutorial/desktop/${step.image}`}
                alt=""
              /> */}
              <h3 className="text-white font-semibold mb-4">
                {t("step", { n: step.n })}
              </h3>
              <p className="text-dark-secondary text-sm">
                {typeof step.text === "string" ? t(step.text) : step.text}
              </p>
            </div>
          ))}
          <div className="w-60 mt-auto">
            <h3 className="text-white font-semibold mb-4">
              {t("step", { n: 4 })}
            </h3>
            <CTAButton
              onClick={handleStartImportClick}
              label={t("tutorial.secondPart.startImportCta")}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
